import {
    CalculatorPresentationContractError,
    requirePresentationArray,
    requirePresentationBoolean,
    requirePresentationFields,
    requirePresentationFinite,
    requirePresentationRecord,
    requirePresentationString,
} from "./calculator-presentation-contract.js";
import {
    calculatorDataEnumLabel as enumLabel,
    calculatorDataText as t,
} from "./calculator-data-i18n.js";
import {
    canonicalizeCalculatorFishingSourceV1,
} from "./calculator-fishing-source.js";
import { validateCalculatorFactKeyShapeV1 } from "./calculator-fact-key-contract.js";
import { CALCULATOR_GROUP_ROLES_V1 } from "../calculator-group-role-v1.js";
import {
    calculatorLootProvenanceQuantityValueText,
    calculatorLootProvenanceRateValueText,
    calculatorLootProvenanceScalarConditionsText,
    calculatorLootProvenanceUnavailableValueText,
} from "./calculator-loot-provenance-presentation.js";

const FACT_KINDS = new Set([
    "groupMembership",
    "groupPresence",
    "optionSelectionRule",
    "itemPresence",
    "itemRate",
    "itemQuantity",
    "itemQuantityDefault",
    "groupRate",
    "itemPrice",
    "tradeCurve",
    "itemEffect",
    "petOption",
    "tradeFact",
    "refuteEvidence",
    "fishingSourceBiteTime",
    "masteryPrizeRate",
    "durabilityReduction",
    "itemIdentity",
    "itemExperience",
]);
const NUMERIC_UNITS = new Set([
    "scalar",
    "seconds",
    "hours",
    "count",
    "perHour",
    "perDay",
    "silver",
    "experience",
    "percent",
    "ratio",
    "multiplier",
    "durability",
    "days",
]);
const PRESENCE_VALUES = new Set(["present", "absent", "unknown"]);
const MEMBERSHIP_VALUES = new Set(["member", "notMember", "unknown"]);
const GROUP_ROLES = new Set(CALCULATOR_GROUP_ROLES_V1);
const EFFECT_KINDS = new Set([
    "autoFishingTimeReduction",
    "rareGroupBonus",
    "highQualityGroupBonus",
    "durabilityReductionResistance",
    "fishMultiplier",
    "fishingExperienceBonus",
    "lifeExperienceBonus",
]);
const PET_OPTION_KINDS = new Set(["special", "talent", "learnedSkill"]);
const ITEM_GRADES = new Set([
    "prize",
    "rare",
    "highQuality",
    "general",
    "trash",
    "white",
    "green",
    "blue",
    "yellow",
    "orange",
    "red",
    "unknown",
]);
const U8_MAX = 255;
const U16_MAX = 65_535;
const U32_MAX = 4_294_967_295;
const PRESENTATION_FIELDS = Object.freeze([
    "conditionsByOperationId",
    "groupsByGroupId",
    "itemsByItemId",
    "labels",
    "locale",
    "numberFormats",
    "provenance",
    "unknownItem",
]);
const COMMON_FACT_FIELDS = Object.freeze(["kind", "operationId", "factId", "provenance"]);
const OPERATION_SHAPES = Object.freeze({
    groupMembership: Object.freeze({
        required: [...COMMON_FACT_FIELDS, "fishingSource", "itemId", "groupId", "membership", "conditions"],
    }),
    groupPresence: Object.freeze({
        required: [...COMMON_FACT_FIELDS, "fishingSource", "groupId", "role", "presence"],
        optional: ["conditions"],
    }),
    optionSelectionRule: Object.freeze({
        required: [...COMMON_FACT_FIELDS, "optionId", "selectionGroupId", "priority"],
    }),
    itemPresence: Object.freeze({
        required: [...COMMON_FACT_FIELDS, "fishingSource", "itemId", "presence"],
        optional: ["conditions"],
    }),
    itemRate: Object.freeze({
        required: [
            ...COMMON_FACT_FIELDS,
            "fishingSource",
            "itemId",
            "groupId",
            "rate",
            "branchId",
        ],
        optional: [
            "conditions",
            "alternative",
        ],
    }),
    itemQuantity: Object.freeze({
        required: [
            ...COMMON_FACT_FIELDS,
            "fishingSource",
            "itemId",
            "groupId",
            "quantity",
        ],
    }),
    itemQuantityDefault: Object.freeze({
        required: [
            ...COMMON_FACT_FIELDS,
            "itemId",
            "quantity",
        ],
    }),
    groupRate: Object.freeze({
        required: [
            ...COMMON_FACT_FIELDS,
            "fishingSource",
            "groupId",
            "rate",
            "conditions",
            "branchId",
        ],
        optional: ["alternative"],
    }),
    itemPrice: Object.freeze({
        required: [...COMMON_FACT_FIELDS, "itemId", "price"],
    }),
    tradeCurve: Object.freeze({
        required: [...COMMON_FACT_FIELDS, "itemId", "pricePercent"],
    }),
    itemEffect: Object.freeze({
        required: [...COMMON_FACT_FIELDS, "subject", "effect", "value"],
    }),
    petOption: Object.freeze({
        required: [
            ...COMMON_FACT_FIELDS,
            "petId",
            "tier",
            "optionId",
            "optionKind",
            "learnChance",
            "effects",
        ],
    }),
    tradeFact: Object.freeze({
        required: [...COMMON_FACT_FIELDS, "fact"],
    }),
    refuteEvidence: Object.freeze({
        required: ["kind", "operationId", "provenance", "targetFactId", "reasonCode"],
    }),
    fishingSourceBiteTime: Object.freeze({
        required: [...COMMON_FACT_FIELDS, "fishingSource", "minimum", "maximum"],
    }),
    masteryPrizeRate: Object.freeze({
        required: [...COMMON_FACT_FIELDS, "mastery", "rate"],
    }),
    durabilityReduction: Object.freeze({
        required: [...COMMON_FACT_FIELDS, "lifeskillLevel", "rate"],
    }),
    itemIdentity: Object.freeze({
        required: [
            ...COMMON_FACT_FIELDS,
            "itemId",
            "catalogOptionId",
            "grade",
            "isFish",
        ],
    }),
    itemExperience: Object.freeze({
        required: [
            ...COMMON_FACT_FIELDS,
            "itemId",
            "fishingExperience",
            "lifeExperience",
            "totemExperience",
        ],
    }),
});
const CONDITION_KINDS = new Set([
    "groupMembership",
    "groupPresence",
    "itemPresence",
    "itemRate",
    "groupRate",
]);

function requireRecord(value, path) {
    return requirePresentationRecord(value, path);
}

function requireShape(value, required, optional, path) {
    const record = requireRecord(value, path);
    const allowed = new Set([...required, ...(optional ?? [])]);
    const unknown = Object.keys(record).find((field) => !allowed.has(field));
    if (unknown) {
        throw new CalculatorPresentationContractError(`${path}.${unknown}`, "unknown field");
    }
    const missing = required.find(
        (field) => !Object.prototype.hasOwnProperty.call(record, field),
    );
    if (missing) {
        throw new CalculatorPresentationContractError(`${path}.${missing}`, "missing field");
    }
    return record;
}

function requireField(record, key, path) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
        throw new CalculatorPresentationContractError(`${path}.${key}`, "missing field");
    }
    return record[key];
}

function requireString(value, path) {
    return requirePresentationString(value, path);
}

function requireNonBlankString(value, path) {
    if (typeof value !== "string" || !value.trim()) {
        throw new CalculatorPresentationContractError(path, "expected a non-blank string");
    }
    return value;
}

function requireFishingSource(value, path) {
    return canonicalizeCalculatorFishingSourceV1(
        value,
        path,
        (issuePath, _code, message) => {
            throw new CalculatorPresentationContractError(issuePath, message);
        },
    );
}

function requireNullableString(value, path) {
    return value === null ? null : requireString(value, path);
}

function requireInteger(value, path, { minimum = 0, maximum = Number.MAX_SAFE_INTEGER } = {}) {
    const parsed = requirePresentationFinite(value, path, { minimum, maximum });
    if (!Number.isSafeInteger(parsed)) {
        throw new CalculatorPresentationContractError(path, "expected a safe integer");
    }
    return parsed;
}

function requireEnum(value, values, path) {
    const parsed = requireString(value, path);
    if (!values.has(parsed)) {
        throw new CalculatorPresentationContractError(path, "unsupported value");
    }
    return parsed;
}

function literalNumber(value, path, { minimum = -Infinity, maximum = Infinity } = {}) {
    return String(requirePresentationFinite(value, path, { minimum, maximum }));
}

function exactValueKey(value) {
    if (Array.isArray(value)) {
        return `[${value.map(exactValueKey).join(",")}]`;
    }
    if (value && typeof value === "object") {
        return `{${Object.keys(value)
            .sort()
            .map((key) => `${key}:${exactValueKey(value[key])}`)
            .join(",")}}`;
    }
    return JSON.stringify(value);
}

function requireMatchingField(factKey, operation, key, factPath, operationPath) {
    const expected = requireField(factKey, key, factPath);
    const actual = requireField(operation, key, operationPath);
    if (exactValueKey(actual) !== exactValueKey(expected)) {
        throw new CalculatorPresentationContractError(
            `${operationPath}.${key}`,
            `does not match ${factPath}.${key}`,
        );
    }
    return actual;
}

function parsePresentation(value) {
    if (value === null) {
        return Object.freeze({
            groupsByGroupId: Object.freeze({}),
            itemsByItemId: Object.freeze({}),
        });
    }
    const presentation = requirePresentationFields(value, PRESENTATION_FIELDS, "presentation");
    return Object.freeze({
        groupsByGroupId: requireRecord(
            presentation.groupsByGroupId,
            "presentation.groupsByGroupId",
        ),
        itemsByItemId: requireRecord(
            presentation.itemsByItemId,
            "presentation.itemsByItemId",
        ),
    });
}

function formatNumeric(value, path) {
    const numeric = requireRecord(value, path);
    const state = requireString(requireField(numeric, "state", path), `${path}.state`);
    if (state === "unknown") {
        requirePresentationFields(numeric, ["state"], path);
        return enumLabel("unknown");
    }
    if (state === "unavailable") {
        return calculatorLootProvenanceUnavailableValueText(numeric, path);
    }
    if (state !== "known") {
        throw new CalculatorPresentationContractError(
            `${path}.state`,
            "unsupported numeric state",
        );
    }
    const known = requirePresentationFields(numeric, ["state", "unit", "value"], path);
    const unit = requireEnum(known.unit, NUMERIC_UNITS, `${path}.unit`);
    return `${literalNumber(known.value, `${path}.value`, { minimum: 0 })} · ${enumLabel(unit)}`;
}

function presentedIdentity(table, id, path) {
    if (!Object.prototype.hasOwnProperty.call(table, String(id))) {
        return String(id);
    }
    const entry = requireRecord(table[String(id)], path);
    const label = requireString(requireField(entry, "label", path), `${path}.label`);
    return `${label} (${id})`;
}

function field(label, value) {
    return `${enumLabel(label)}: ${value}`;
}

function formatAlternativeApplicability(value, path) {
    const alternative = requirePresentationFields(
        value,
        ["alternativeId", "applicability", "familyId", "priority"],
        path,
    );
    requireString(alternative.familyId, `${path}.familyId`);
    requireString(alternative.alternativeId, `${path}.alternativeId`);
    requireInteger(alternative.priority, `${path}.priority`, { maximum: U32_MAX });
    const applicabilityPath = `${path}.applicability`;
    const applicability = requireRecord(alternative.applicability, applicabilityPath);
    const kind = requireString(
        requireField(applicability, "kind", applicabilityPath),
        `${applicabilityPath}.kind`,
    );
    if (kind === "fallback") {
        requirePresentationFields(applicability, ["kind"], applicabilityPath);
        return t("common.status.default", "Default");
    }
    if (kind === "typedConditions") {
        const typed = requirePresentationFields(
            applicability,
            ["conditions", "kind"],
            applicabilityPath,
        );
        const conditions = requirePresentationArray(
            typed.conditions,
            `${applicabilityPath}.conditions`,
        );
        if (!conditions.length) {
            throw new CalculatorPresentationContractError(
                `${applicabilityPath}.conditions`,
                "must contain at least one condition",
            );
        }
        return calculatorLootProvenanceScalarConditionsText(
            conditions,
            `${applicabilityPath}.conditions`,
        );
    }
    if (kind === "sourceExpression") {
        const source = requirePresentationFields(
            applicability,
            ["expression", "kind"],
            applicabilityPath,
        );
        return requireNonBlankString(
            source.expression,
            `${applicabilityPath}.expression`,
        );
    }
    throw new CalculatorPresentationContractError(
        `${applicabilityPath}.kind`,
        "expected fallback, typedConditions, or sourceExpression",
    );
}

function formatAlternativeAudit(value, path) {
    const alternative = requirePresentationFields(
        value,
        ["alternativeId", "applicability", "familyId", "priority"],
        path,
    );
    const applicability = requireRecord(
        alternative.applicability,
        `${path}.applicability`,
    );
    formatAlternativeApplicability(value, path);
    return [
        field("familyId", requireString(alternative.familyId, `${path}.familyId`)),
        field("alternativeId", requireString(
            alternative.alternativeId,
            `${path}.alternativeId`,
        )),
        field("priority", requireInteger(
            alternative.priority,
            `${path}.priority`,
            { maximum: U32_MAX },
        )),
        field("applicability", requireString(
            applicability.kind,
            `${path}.applicability.kind`,
        )),
    ].join(" · ");
}

function subjectText(subject, presentation, path) {
    const value = requireRecord(subject, path);
    const scope = requireString(requireField(value, "scope", path), `${path}.scope`);
    if (scope === "item") {
        const item = requirePresentationFields(value, ["itemId", "scope"], path);
        const itemId = requireInteger(item.itemId, `${path}.itemId`, {
            minimum: 1,
            maximum: U32_MAX,
        });
        return field(
            "item",
            presentedIdentity(
                presentation.itemsByItemId,
                itemId,
                `presentation.itemsByItemId.${itemId}`,
            ),
        );
    }
    if (scope === "option") {
        const option = requirePresentationFields(value, ["optionId", "scope"], path);
        return field("option", requireString(option.optionId, `${path}.optionId`));
    }
    throw new CalculatorPresentationContractError(`${path}.scope`, "unsupported effect subject");
}

function groupText(groupId, presentation) {
    return presentedIdentity(
        presentation.groupsByGroupId,
        groupId,
        `presentation.groupsByGroupId.${groupId}`,
    );
}

function petEffectText(effect, path) {
    const value = requirePresentationFields(effect, ["effect", "value"], path);
    const kind = requireEnum(value.effect, EFFECT_KINDS, `${path}.effect`);
    return `${enumLabel(kind)}: ${formatNumeric(value.value, `${path}.value`)}`;
}

function validateTradeFactKey(value, path) {
    const fact = requireRecord(value, path);
    const kind = requireString(requireField(fact, "kind", path), `${path}.kind`);
    if (kind === "origin") {
        const origin = requirePresentationFields(fact, ["kind", "originId"], path);
        requireString(origin.originId, `${path}.originId`);
        return origin;
    }
    if (kind === "destination") {
        const destination = requirePresentationFields(fact, ["destinationId", "kind"], path);
        requireString(destination.destinationId, `${path}.destinationId`);
        return destination;
    }
    if (kind === "zoneOriginAffinity") {
        const affinity = requirePresentationFields(
            fact,
            ["kind", "originId", "zoneId"],
            path,
        );
        requireString(affinity.zoneId, `${path}.zoneId`);
        requireString(affinity.originId, `${path}.originId`);
        return affinity;
    }
    if (kind === "bargainBonus") {
        const bargain = requirePresentationFields(fact, ["kind", "tradeLevel"], path);
        requireInteger(bargain.tradeLevel, `${path}.tradeLevel`, { maximum: U16_MAX });
        return bargain;
    }
    throw new CalculatorPresentationContractError(`${path}.kind`, "unsupported trade fact");
}

function tradeFactText(factKey, operation, factPath, operationPath) {
    const key = validateTradeFactKey(requireField(factKey, "fact", factPath), `${factPath}.fact`);
    const fact = requireRecord(requireField(operation, "fact", operationPath), `${operationPath}.fact`);
    const kind = requireString(requireField(fact, "kind", `${operationPath}.fact`), `${operationPath}.fact.kind`);
    if (key.kind !== kind) {
        throw new CalculatorPresentationContractError(
            `${operationPath}.fact.kind`,
            `does not match ${factPath}.fact.kind`,
        );
    }
    if (kind === "origin") {
        const origin = requirePresentationFields(
            fact,
            ["kind", "originId", "worldX", "worldZ"],
            `${operationPath}.fact`,
        );
        requireMatchingField(
            key,
            origin,
            "originId",
            `${factPath}.fact`,
            `${operationPath}.fact`,
        );
        return [
            field("origin", origin.originId),
            field("worldX", literalNumber(origin.worldX, `${operationPath}.fact.worldX`)),
            field("worldZ", literalNumber(origin.worldZ, `${operationPath}.fact.worldZ`)),
        ].join(" · ");
    }
    if (kind === "destination") {
        const destination = requirePresentationFields(
            fact,
            ["destinationId", "kind", "worldX", "worldZ"],
            `${operationPath}.fact`,
        );
        requireMatchingField(
            key,
            destination,
            "destinationId",
            `${factPath}.fact`,
            `${operationPath}.fact`,
        );
        return [
            field("destination", destination.destinationId),
            field("worldX", literalNumber(destination.worldX, `${operationPath}.fact.worldX`)),
            field("worldZ", literalNumber(destination.worldZ, `${operationPath}.fact.worldZ`)),
        ].join(" · ");
    }
    if (kind === "zoneOriginAffinity") {
        const affinity = requirePresentationFields(
            fact,
            ["kind", "originId", "pixelCount", "zoneId"],
            `${operationPath}.fact`,
        );
        requireMatchingField(
            key,
            affinity,
            "zoneId",
            `${factPath}.fact`,
            `${operationPath}.fact`,
        );
        requireMatchingField(
            key,
            affinity,
            "originId",
            `${factPath}.fact`,
            `${operationPath}.fact`,
        );
        return [
            field("zone", affinity.zoneId),
            field("origin", affinity.originId),
            field(
                "pixelCount",
                requireInteger(affinity.pixelCount, `${operationPath}.fact.pixelCount`, {
                    maximum: U32_MAX,
                }),
            ),
        ].join(" · ");
    }
    if (kind === "bargainBonus") {
        const bargain = requirePresentationFields(
            fact,
            ["kind", "rate", "tradeLevel"],
            `${operationPath}.fact`,
        );
        requireMatchingField(
            key,
            bargain,
            "tradeLevel",
            `${factPath}.fact`,
            `${operationPath}.fact`,
        );
        return [
            field("tradeLevel", bargain.tradeLevel),
            field(
                "rate",
                calculatorLootProvenanceRateValueText(
                    bargain.rate,
                    `${operationPath}.fact.rate`,
                ),
            ),
        ].join(" · ");
    }
    throw new CalculatorPresentationContractError(
        `${operationPath}.fact.kind`,
        "unsupported trade fact",
    );
}

function validateOperationShape(value, path) {
    const operation = requireRecord(value, path);
    const kind = requireEnum(
        requireField(operation, "kind", path),
        FACT_KINDS,
        `${path}.kind`,
    );
    const shape = OPERATION_SHAPES[kind];
    const parsed = requireShape(operation, shape.required, shape.optional, path);
    requireString(parsed.operationId, `${path}.operationId`);
    if (kind !== "refuteEvidence") {
        requireString(parsed.factId, `${path}.factId`);
    }
    requireRecord(parsed.provenance, `${path}.provenance`);
    if (
        CONDITION_KINDS.has(kind)
        || kind === "itemQuantity"
        || kind === "fishingSourceBiteTime"
    ) {
        requireFishingSource(parsed.fishingSource, `${path}.fishingSource`);
    }
    return parsed;
}

function validateFactKey(value, path) {
    const parsed = validateCalculatorFactKeyShapeV1(value, path, {
        fail(issuePath, message) {
            throw new CalculatorPresentationContractError(issuePath, message);
        },
        requireExactFields: requirePresentationFields,
        requireRecord,
        requireString,
    });
    const kind = requireEnum(parsed.kind, FACT_KINDS, `${path}.kind`);
    switch (kind) {
        case "groupMembership":
        case "itemRate":
        case "itemQuantity":
            requireInteger(parsed.itemId, `${path}.itemId`, { minimum: 1, maximum: U32_MAX });
            requireString(parsed.groupId, `${path}.groupId`);
            requireFishingSource(parsed.fishingSource, `${path}.fishingSource`);
            if (kind === "itemRate") {
                requireNullableString(parsed.branchId, `${path}.branchId`);
            }
            break;
        case "groupPresence":
            requireFishingSource(parsed.fishingSource, `${path}.fishingSource`);
            requireString(parsed.groupId, `${path}.groupId`);
            break;
        case "optionSelectionRule":
            requireString(parsed.optionId, `${path}.optionId`);
            break;
        case "itemPresence":
            requireFishingSource(parsed.fishingSource, `${path}.fishingSource`);
            requireInteger(parsed.itemId, `${path}.itemId`, { minimum: 1, maximum: U32_MAX });
            break;
        case "groupRate":
            requireFishingSource(parsed.fishingSource, `${path}.fishingSource`);
            requireString(parsed.groupId, `${path}.groupId`);
            requireNullableString(parsed.branchId, `${path}.branchId`);
            break;
        case "itemPrice":
        case "itemQuantityDefault":
        case "tradeCurve":
        case "itemIdentity":
        case "itemExperience":
            requireInteger(parsed.itemId, `${path}.itemId`, { minimum: 1, maximum: U32_MAX });
            break;
        case "itemEffect":
            subjectText(parsed.subject, {
                itemsByItemId: Object.freeze({}),
            }, `${path}.subject`);
            requireEnum(parsed.effect, EFFECT_KINDS, `${path}.effect`);
            break;
        case "petOption":
            requireString(parsed.petId, `${path}.petId`);
            requireInteger(parsed.tier, `${path}.tier`, { maximum: U8_MAX });
            requireString(parsed.optionId, `${path}.optionId`);
            requireEnum(parsed.optionKind, PET_OPTION_KINDS, `${path}.optionKind`);
            break;
        case "tradeFact":
            validateTradeFactKey(parsed.fact, `${path}.fact`);
            break;
        case "refuteEvidence":
            requireString(parsed.targetFactId, `${path}.targetFactId`);
            break;
        case "fishingSourceBiteTime":
            requireFishingSource(parsed.fishingSource, `${path}.fishingSource`);
            break;
        case "masteryPrizeRate":
            requireInteger(parsed.mastery, `${path}.mastery`, { maximum: U32_MAX });
            break;
        case "durabilityReduction":
            requireInteger(parsed.lifeskillLevel, `${path}.lifeskillLevel`, {
                maximum: U16_MAX,
            });
            break;
        default:
            break;
    }
    return parsed;
}

function factValueText(factKeyValue, operationValue, presentation, path) {
    const factPath = `${path}.factKey`;
    const operationPath = path;
    const key = validateFactKey(factKeyValue, factPath);
    const value = validateOperationShape(operationValue, operationPath);
    const kind = key.kind;
    if (value.kind !== kind) {
        throw new CalculatorPresentationContractError(
            `${operationPath}.kind`,
            `does not match ${factPath}.kind`,
        );
    }

    switch (kind) {
        case "groupMembership":
            requireMatchingField(key, value, "fishingSource", factPath, operationPath);
            requireMatchingField(key, value, "itemId", factPath, operationPath);
            requireMatchingField(key, value, "groupId", factPath, operationPath);
            return enumLabel(
                requireEnum(value.membership, MEMBERSHIP_VALUES, `${operationPath}.membership`),
            );
        case "groupPresence":
            requireMatchingField(key, value, "fishingSource", factPath, operationPath);
            requireMatchingField(key, value, "groupId", factPath, operationPath);
            requireEnum(value.role, GROUP_ROLES, `${operationPath}.role`);
            return enumLabel(
                requireEnum(value.presence, PRESENCE_VALUES, `${operationPath}.presence`),
            );
        case "optionSelectionRule":
            requireMatchingField(key, value, "optionId", factPath, operationPath);
            return [
                field(
                    "selectionGroupId",
                    requireString(value.selectionGroupId, `${operationPath}.selectionGroupId`),
                ),
                field(
                    "priority",
                    requireInteger(value.priority, `${operationPath}.priority`, {
                        maximum: U32_MAX,
                    }),
                ),
            ].join(" · ");
        case "itemPresence":
            requireMatchingField(key, value, "fishingSource", factPath, operationPath);
            requireMatchingField(key, value, "itemId", factPath, operationPath);
            return enumLabel(
                requireEnum(value.presence, PRESENCE_VALUES, `${operationPath}.presence`),
            );
        case "itemRate":
            requireMatchingField(key, value, "fishingSource", factPath, operationPath);
            requireMatchingField(key, value, "itemId", factPath, operationPath);
            requireMatchingField(key, value, "groupId", factPath, operationPath);
            requireMatchingField(key, value, "branchId", factPath, operationPath);
            return calculatorLootProvenanceRateValueText(
                value.rate,
                `${operationPath}.rate`,
            );
        case "itemQuantity": {
            requireMatchingField(key, value, "fishingSource", factPath, operationPath);
            requireMatchingField(key, value, "itemId", factPath, operationPath);
            requireMatchingField(key, value, "groupId", factPath, operationPath);
            return calculatorLootProvenanceQuantityValueText(
                value.quantity,
                {
                    locale: presentation.locale,
                    path: `${operationPath}.quantity`,
                },
            );
        }
        case "itemQuantityDefault": {
            requireMatchingField(key, value, "itemId", factPath, operationPath);
            return calculatorLootProvenanceQuantityValueText(
                value.quantity,
                {
                    locale: presentation.locale,
                    path: `${operationPath}.quantity`,
                },
            );
        }
        case "groupRate":
            requireMatchingField(key, value, "fishingSource", factPath, operationPath);
            requireMatchingField(key, value, "groupId", factPath, operationPath);
            requireMatchingField(key, value, "branchId", factPath, operationPath);
            return calculatorLootProvenanceRateValueText(
                value.rate,
                `${operationPath}.rate`,
            );
        case "itemPrice":
            requireMatchingField(key, value, "itemId", factPath, operationPath);
            return formatNumeric(value.price, `${operationPath}.price`);
        case "tradeCurve":
            requireMatchingField(key, value, "itemId", factPath, operationPath);
            return calculatorLootProvenanceRateValueText(
                value.pricePercent,
                `${operationPath}.pricePercent`,
            );
        case "itemEffect":
            requireMatchingField(key, value, "subject", factPath, operationPath);
            requireMatchingField(key, value, "effect", factPath, operationPath);
            return [
                subjectText(value.subject, presentation, `${operationPath}.subject`),
                `${enumLabel(
                    requireEnum(value.effect, EFFECT_KINDS, `${operationPath}.effect`),
                )}: ${formatNumeric(value.value, `${operationPath}.value`)}`,
            ].join(" · ");
        case "petOption": {
            requireMatchingField(key, value, "petId", factPath, operationPath);
            requireMatchingField(key, value, "tier", factPath, operationPath);
            requireMatchingField(key, value, "optionId", factPath, operationPath);
            requireMatchingField(key, value, "optionKind", factPath, operationPath);
            requireString(value.petId, `${operationPath}.petId`);
            requireInteger(value.tier, `${operationPath}.tier`, { maximum: U8_MAX });
            requireString(value.optionId, `${operationPath}.optionId`);
            requireEnum(value.optionKind, PET_OPTION_KINDS, `${operationPath}.optionKind`);
            const effects = requirePresentationArray(value.effects, `${operationPath}.effects`);
            const effectText = effects.length
                ? effects.map((effect, index) => petEffectText(
                    effect,
                    `${operationPath}.effects[${index}]`,
                )).join(" · ")
                : t("calculator.data.none", "None");
            return [
                field("pet", value.petId),
                field("tier", value.tier),
                field("option", value.optionId),
                field("optionKind", enumLabel(value.optionKind)),
                field(
                    "learnChance",
                    calculatorLootProvenanceRateValueText(
                        value.learnChance,
                        `${operationPath}.learnChance`,
                    ),
                ),
                field("effects", effectText),
            ].join(" · ");
        }
        case "tradeFact":
            return tradeFactText(key, value, factPath, operationPath);
        case "refuteEvidence":
            requireMatchingField(key, value, "targetFactId", factPath, operationPath);
            return [
                field("targetFactId", value.targetFactId),
                field(
                    "reasonCode",
                    requireString(value.reasonCode, `${operationPath}.reasonCode`),
                ),
            ].join(" · ");
        case "fishingSourceBiteTime":
            requireMatchingField(key, value, "fishingSource", factPath, operationPath);
            return `${formatNumeric(
                value.minimum,
                `${operationPath}.minimum`,
            )} – ${formatNumeric(
                value.maximum,
                `${operationPath}.maximum`,
            )}`;
        case "masteryPrizeRate":
            requireMatchingField(key, value, "mastery", factPath, operationPath);
            requireInteger(value.mastery, `${operationPath}.mastery`, { maximum: U32_MAX });
            return [
                field("mastery", value.mastery),
                field(
                    "rate",
                    calculatorLootProvenanceRateValueText(
                        value.rate,
                        `${operationPath}.rate`,
                    ),
                ),
            ].join(" · ");
        case "durabilityReduction":
            requireMatchingField(key, value, "lifeskillLevel", factPath, operationPath);
            requireInteger(value.lifeskillLevel, `${operationPath}.lifeskillLevel`, {
                maximum: U16_MAX,
            });
            return [
                field("lifeskillLevel", value.lifeskillLevel),
                field(
                    "rate",
                    calculatorLootProvenanceRateValueText(
                        value.rate,
                        `${operationPath}.rate`,
                    ),
                ),
            ].join(" · ");
        case "itemIdentity":
            requireMatchingField(key, value, "itemId", factPath, operationPath);
            return [
                field(
                    "grade",
                    enumLabel(requireEnum(value.grade, ITEM_GRADES, `${operationPath}.grade`)),
                ),
                field(
                    "isFish",
                    enumLabel(String(requirePresentationBoolean(
                        value.isFish,
                        `${operationPath}.isFish`,
                    ))),
                ),
                field(
                    "catalogOptionId",
                    requireNullableString(
                        value.catalogOptionId,
                        `${operationPath}.catalogOptionId`,
                    ) ?? t("calculator.data.none", "None"),
                ),
            ].join(" · ");
        case "itemExperience":
            requireMatchingField(key, value, "itemId", factPath, operationPath);
            return [
                field(
                    "fishingExperience",
                    formatNumeric(
                        value.fishingExperience,
                        `${operationPath}.fishingExperience`,
                    ),
                ),
                field(
                    "lifeExperience",
                    formatNumeric(value.lifeExperience, `${operationPath}.lifeExperience`),
                ),
                field(
                    "totemExperience",
                    formatNumeric(value.totemExperience, `${operationPath}.totemExperience`),
                ),
            ].join(" · ");
        default:
            throw new CalculatorPresentationContractError(
                `${factPath}.kind`,
                "unsupported fact kind",
            );
    }
}

function row(labelKey, fallback, value) {
    return Object.freeze({
        label: t(labelKey, fallback),
        value: String(value),
        icon: "",
        tone: "",
    });
}

function qualifierRows(operation, path) {
    const rows = [];
    if (CONDITION_KINDS.has(operation.kind)) {
        const hasConditions = Object.prototype.hasOwnProperty.call(operation, "conditions");
        const conditions = hasConditions
            ? requirePresentationArray(operation.conditions, `${path}.conditions`)
            : [];
        const hasAlternative = Object.prototype.hasOwnProperty.call(operation, "alternative");
        const directConditions = conditions.length
            ? calculatorLootProvenanceScalarConditionsText(
                conditions,
                `${path}.conditions`,
            )
            : null;
        const alternativeApplicability = hasAlternative
            ? formatAlternativeApplicability(
                operation.alternative,
                `${path}.alternative`,
            )
            : null;
        const formatted = [directConditions, alternativeApplicability]
            .filter(Boolean)
            .join(" · ") || null;
        if (formatted !== null) {
            rows.push(row(
                "calculator.data.metadata.conditions",
                "Conditions",
                formatted,
            ));
        }
    }
    if (operation.kind === "itemRate" || operation.kind === "groupRate") {
        const branchId = requireNullableString(operation.branchId, `${path}.branchId`);
        if (branchId !== null) {
            rows.push(row(
                "calculator.data.metadata.branch_id",
                "Branch ID",
                branchId,
            ));
        }
        if (Object.prototype.hasOwnProperty.call(operation, "alternative")) {
            rows.push(row(
                "calculator.data.metadata.alternative",
                "Alternative",
                formatAlternativeAudit(operation.alternative, `${path}.alternative`),
            ));
        }
    }
    return rows;
}

export function createCalculatorProvenanceFactPresenter(dataPresentation = null) {
    const presentation = parsePresentation(dataPresentation);
    return Object.freeze({
        rows(factKey, operation, path = "projection.provenanceTrace.candidate.operation") {
            const value = factValueText(factKey, operation, presentation, path);
            return Object.freeze([
                row("calculator.data.metadata.value", "Value", value),
                ...qualifierRows(operation, path),
            ]);
        },
    });
}
